/* eslint-disable react/no-array-index-key */
import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage } from 'react-intl';

import chunk from 'lodash/chunk';

import { DrawerContext, IconRegularPlus, Text } from '@notino/react-styleguide';
import { WishlistList } from '@notino/shared/definitions/types';

import { Divider } from '@components/Divider';
import wishlistModifierMessages from '@components/Universals/WishlistModifier/messages';
import { WISHLIST_BASE_URL } from '@constants';

import wishlistMessages from '../WishList/messages';

import { ListMenuDrawer } from './components/ListMenuDrawer';
import { LogInBanner } from './components/LogInBanner';
import { Thumbnails } from './components/Thumbnails';
import { useNewListDrawer } from './components/useNewListDrawer';
import { useListsData } from './hooks/useListsData';
import { useListThumbnails } from './hooks/useListThumbnails';
import messages from './messages';
import {
  HideDivider,
  ItemTop,
  ListChunksContainer,
  ListItem,
  ListsContainer,
  NewListButton,
  Options,
  PageDescription,
  PageTitle,
  WishlistListsPageWrapper,
} from './styled';

const WishlistLists = () => {
  const { lists } = useListsData();
  const { open: openNewListDrawer } = useNewListDrawer();
  const [listMenuList, setListMenuList] = React.useState<WishlistList>();

  const listsChunks = chunk(lists, 6);

  return (
    <WishlistListsPageWrapper>
      <PageTitle>
        <FormattedMessage {...messages.listsPageTitle} />
      </PageTitle>
      <PageDescription>
        <FormattedMessage {...messages.listsPageDescription} />
      </PageDescription>

      <LogInBanner />

      <NewListButton onClick={openNewListDrawer}>
        <IconRegularPlus width="20px" height="20px" />
        <span>
          <FormattedMessage {...wishlistModifierMessages.newListButton} />
        </span>
      </NewListButton>

      <ListChunksContainer>
        {listsChunks.map((listChunk, chunkIndex) => (
          <ListsChunks
            key={chunkIndex}
            lists={listChunk}
            isFirst={chunkIndex === 0}
            isLast={chunkIndex === listsChunks.length - 1}
            setListMenuList={setListMenuList}
          />
        ))}
      </ListChunksContainer>

      {listMenuList && (
        <ListMenuDrawer
          list={listMenuList}
          onClose={() => setListMenuList(undefined)}
        />
      )}
    </WishlistListsPageWrapper>
  );
};

const ListsChunks = ({
  isFirst,
  lists,
  isLast,
  setListMenuList,
}: {
  lists: WishlistList[];
  isFirst: boolean;
  isLast: boolean;
  setListMenuList: React.Dispatch<React.SetStateAction<WishlistList>>;
}) => {
  const { inView, ref } = useInView({ triggerOnce: true });
  const { getThumbnails } = useListThumbnails({ lists, inView });

  return (
    <ListsContainer ref={ref}>
      {lists.map((list, i) => (
        <ListItem
          key={list.id}
          href={`${WISHLIST_BASE_URL}/${list.id}`}
          isFirst={isFirst && i === 0}
          isLast={isLast && i === lists.length - 1}
        >
          <ItemTop>
            <Text
              as="h6"
              typography="labelLarge"
              block={true}
              marginBottom="2px"
              color="text.primary"
              style={{ wordBreak: 'break-word' }}
            >
              {list.name}
            </Text>
            <Text typography="labelRegular400" color="text.tertiary">
              <FormattedMessage {...wishlistMessages.productsCount} />:{' '}
              {list.products.length}
            </Text>
          </ItemTop>

          <Options
            color="icon.tertiary"
            onClick={(e) => {
              e.preventDefault();
              setListMenuList(list);
            }}
          />

          {list.products.length > 0 && (
            <HideDivider breakpoint="md">
              <Divider color="border.divider" />
            </HideDivider>
          )}

          <Thumbnails
            productsCount={list.products.length}
            thumbnails={getThumbnails(list.id)}
          />
        </ListItem>
      ))}
    </ListsContainer>
  );
};

const Providers = () => (
  <DrawerContext.Provider>
    <WishlistLists />
  </DrawerContext.Provider>
);

export default Providers;
